import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { catchError, debounceTime, map, mergeMap, of, switchMap } from 'rxjs';
import { ArticleKind, ICoin } from '../../models/article.model';
import { CoinApiService } from '../../services/api/coin.service';
import { IErrorResult } from '../../services/dto.service';
import { GoogleService } from '../../services/utility/google.service';
import { AppState, ICoinState } from '../app.state';
import { fromCoinActions } from '../coin/coin.actions';
import { CoinSelector } from '../coin/coin.selectors';
import { fromCrosssellingActions } from '../crossselling/crossselling.actions';
import { EntityEffects } from '../entity.effects';
import { NewspaperSelector } from '../newspaper/newspaper.selectors';
import { RouterSelector } from '../router/router.selectors';

@Injectable()
export class CoinEffects extends EntityEffects<ICoin, ICoinState> {
	constructor(
		actions$: Actions,
		store: Store<AppState>,
		private coinApiService: CoinApiService,
		private coinSelector: CoinSelector,
		routerSelector: RouterSelector,
		private newspaperSelector: NewspaperSelector,
		private googleService: GoogleService
	) {
		super(actions$, store, coinApiService, coinSelector, routerSelector, fromCoinActions);
	}

	public onCrosssellingLoad$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromCrosssellingActions.load),
			debounceTime(10),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.date)),
			switchMap(([, date]) =>
				this.coinApiService
					.search({ date: moment.utc(date).toDate(), dateFrom: moment.utc(date).startOf('year').toDate(), dateUntil: moment.utc(date).endOf('year').startOf('day').toDate(), articleKind: ArticleKind.Coin }, { skip: 0, top: 1 })
					.pipe(
						map(data => fromCrosssellingActions.loaded({ articles: data })),
						catchError((response: IErrorResult) => of(this.entityActions.failed({ message: response.error?.error || response.error?.message })))
					)
			)
		)
	);

	public onLoad$ = createEffect(() =>
		this.actions$.pipe(
			ofType(this.entityActions.load),
			debounceTime(10),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.date)),
			concatLatestFrom(() => this.store.select(this.coinSelector.paging)),
			concatLatestFrom(() => this.store.select(this.coinSelector.dayRange)),
			switchMap(([[[, date], paging], dayRange]) =>
				this.coinApiService
					.search(
						{ date: moment.utc(date).startOf('year').toDate(), dateFrom: moment.utc(date).subtract(dayRange.min, 'days').toDate(), dateUntil: moment.utc(date).add(dayRange.max, 'days').toDate(), articleKind: ArticleKind.Coin },
						paging
					)
					.pipe(
						map(data => this.entityActions.loaded({ list: data })),
						catchError((response: IErrorResult) => of(this.entityActions.failed({ message: response.error?.error || response.error?.message })))
					)
			)
		)
	);

	public onLoadedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(this.entityActions.loaded),
				mergeMap(({ list }) => [this.googleService.viewItemList(list)])
			),
		{ dispatch: false }
	);
}
